import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiZoomIn } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

/**
 * Modern Masonry Gallery Component
 * 
 * HOW IT WORKS:
 * - Uses CSS columns for masonry layout (column-count property)
 * - break-inside-avoid prevents cards from splitting across columns
 * - Responsive column count: 1 → 2 → 3 → 4 columns
 * - Each card auto-adjusts height based on image aspect ratio
 * - Framer Motion provides staggered fade-in animations on scroll
 */

const MasonryGallery = ({ images = [], onImageClick }) => {
  const [loadedImages, setLoadedImages] = useState({});

  const handleImageLoad = (index) => {
    setLoadedImages(prev => ({ ...prev, [index]: true }));
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { 
      opacity: 0, 
      y: 30,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1] // Smooth easing curve
      }
    }
  };

  return (
    <motion.div
      key={`masonry-${images.map(img => img.id).join('-')}`}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6 space-y-6"
    >
      {images.map((image, index) => (
        <motion.div
          key={image.id || index}
          variants={itemVariants}
          className="break-inside-avoid mb-6 group cursor-pointer"
          onClick={() => onImageClick && onImageClick(index)}
        >
          {/* Card Container */}
          <div className="relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100/80">
            {/* Image Container */}
            <div className="relative overflow-hidden">
              {/* Skeleton Loader */}
              {!loadedImages[index] && (
                <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 animate-pulse" />
              )}

              {/* Main Image - With image load reflow trigger */}
              <img
                src={image.url}
                alt={image.title || `Gallery image ${index + 1}`}
                loading="lazy"
                onLoad={(e) => {
                  handleImageLoad(index);
                  // Trigger reflow for masonry recalculation
                  e.target.style.opacity = '1';
                }}
                className={`
                  w-full h-auto object-cover
                  transition-all duration-700 ease-out
                  group-hover:scale-110
                  ${loadedImages[index] ? 'opacity-100' : 'opacity-0'}
                `}
              />

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500">
                {/* Zoom Icon */}
                <div className="absolute top-4 right-4">
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center border border-white/30 transform scale-75 group-hover:scale-100 transition-transform duration-500">
                    <SafeIcon icon={FiZoomIn} className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Text Content */}
                {(image.title || image.category) && (
                  <div className="absolute bottom-0 left-0 right-0 p-5 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                    {image.category && (
                      <span className="inline-block px-3 py-1 bg-emerald-500/90 backdrop-blur-sm text-white text-xs font-semibold rounded-full mb-2">
                        {image.category}
                      </span>
                    )}
                    {image.title && (
                      <h3 className="text-white font-bold text-lg leading-tight line-clamp-2">
                        {image.title}
                      </h3>
                    )}
                    {image.description && (
                      <p className="text-white/80 text-sm mt-1 line-clamp-2">
                        {image.description}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Decorative Corner Accent */}
              <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-emerald-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>
          </div>
        </motion.div>
      ))}
    </motion.div>
  );
};

export default MasonryGallery;

/**
 * TECHNICAL EXPLANATION:
 * 
 * 1. MASONRY LAYOUT:
 *    - CSS columns (column-count) creates Pinterest-style layout
 *    - break-inside-avoid prevents card splitting
 *    - space-y-6 adds vertical spacing between cards
 *    - gap-6 adds horizontal spacing between columns
 * 
 * 2. RESPONSIVE COLUMNS:
 *    - Mobile: columns-1 (1 column)
 *    - Tablet: sm:columns-2 (2 columns at 640px+)
 *    - Laptop: lg:columns-3 (3 columns at 1024px+)
 *    - Desktop: xl:columns-4 (4 columns at 1280px+)
 * 
 * 3. AUTO HEIGHT:
 *    - Images use h-auto to maintain aspect ratio
 *    - object-cover ensures proper image fitting
 *    - No fixed heights - cards adjust naturally
 * 
 * 4. ANIMATIONS:
 *    - Framer Motion staggerChildren creates wave effect
 *    - Each card fades in with y-axis movement
 *    - Hover effects use transform: scale and opacity
 * 
 * 5. PERFORMANCE:
 *    - Lazy loading via loading="lazy" attribute
 *    - Skeleton loader shows during image load
 *    - viewport={{ once: true }} prevents re-animation
 * 
 * 6. ACCESSIBILITY:
 *    - Semantic HTML structure
 *    - Alt text for images
 *    - Keyboard-accessible (cursor-pointer)
 *    - Focus states via Tailwind
 */
